/**
 * Vehicle Mission History Component
 * Displays comprehensive mission history for a vehicle
 * Shows statistics, health trends, and individual mission cards
 */

import React, { useState, useEffect } from 'react';
import { ClipboardList, TrendingUp, BarChart3, Calendar, Package } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { calculateMissionStatistics } from '../utils/missionHistoryUtils';
import MissionCard from './MissionCard';

const VehicleMissionHistory = ({ vehicle, themeClasses }) => {
  const { getThemeClasses } = useTheme();
  const classes = themeClasses || getThemeClasses();
  
  const [missions, setMissions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Load unit system from settings
  const savedSettings = localStorage.getItem('fracadapt-settings');
  let isMetric = true;
  if (savedSettings) {
    try {
      const settings = JSON.parse(savedSettings);
      isMetric = settings.general?.unitSystem === 'Metric';
    } catch (error) {
      console.warn('Failed to load settings');
    }
  }

  // Load mission history and sync status with Saved Missions
  useEffect(() => {
    const loadMissionsWithCurrentStatus = async () => {
      // Fetch fresh vehicle data from API to get latest mission history
      let currentVehicle = vehicle;
      
      if (vehicle && vehicle.id) {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
          const response = await fetch(`${apiUrl}/api/fleet/vehicles/${vehicle.id}`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
            }
          });
          
          if (response.ok) {
            const freshVehicle = await response.json();
            currentVehicle = freshVehicle;
            console.log('✅ Fetched fresh vehicle data from API');
          } else {
            console.warn('⚠️ Failed to fetch fresh vehicle data, using prop');
          }
        } catch (error) {
          console.warn('⚠️ Error fetching fresh vehicle data:', error);
        }
      }
      
      if (currentVehicle && currentVehicle.missionHistory) {
        const sortedMissions = [...currentVehicle.missionHistory].sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        
        // Fetch current status from Saved Missions (backend database)
        console.log('🔄 Syncing mission statuses from backend...');
        console.log('   Vehicle mission history count:', sortedMissions.length);
        
        const missionsWithCurrentStatus = await Promise.all(
          sortedMissions.map(async (mission) => {
            console.log('   Checking mission:', mission.missionName, 'ID:', mission.missionId);
            console.log('   Current stored status:', mission.status);
            
            if (mission.missionId) {
              try {
                // Fetch current mission status from backend API
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
                const response = await fetch(`${apiUrl}/api/missions/${mission.missionId}`, {
                  headers: {
                    'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
                  }
                });
                
                if (response.ok) {
                  const result = await response.json();
                  const currentMission = result.data || result;  // Handle both response formats
                  
                  console.log('   Matching saved mission: FOUND');
                  console.log('   Saved mission status:', currentMission.status);
                  
                  if (currentMission && currentMission.status) {
                    // Return mission with current status from Saved Missions
                    console.log('   ✅ Syncing status:', mission.status, '→', currentMission.status);
                    return {
                      ...mission,
                      status: currentMission.status,  // Use live status
                      _syncedAt: new Date().toISOString()
                    };
                  }
                } else {
                  console.warn('   ⚠️ Mission not found in database (404)');
                }
              } catch (error) {
                console.warn('   ❌ Failed to sync mission status:', error);
              }
            } else {
              console.warn('   ⚠️ Mission has no missionId');
            }
            // Return original mission if sync fails
            console.log('   ⚠️ Using original status:', mission.status);
            return mission;
          })
        );
        
        console.log('✅ Status sync complete');
        
        setMissions(missionsWithCurrentStatus);
        
        // Calculate statistics
        const stats = calculateMissionStatistics(missionsWithCurrentStatus, isMetric);
        setStatistics(stats);
      } else {
        setMissions([]);
        setStatistics(null);
      }
    };
    
    loadMissionsWithCurrentStatus();
  }, [vehicle, isMetric, refreshKey]);

  // Listen for mission status changes from Saved Missions (separate effect)
  useEffect(() => {
    const handleMissionStatusChange = () => {
      console.log('🔔 Mission status changed - refreshing vehicle mission history');
      setRefreshKey(prev => prev + 1);
    };
    
    window.addEventListener('missionStatusChanged', handleMissionStatusChange);
    
    return () => {
      window.removeEventListener('missionStatusChanged', handleMissionStatusChange);
    };
  }, []); // Empty dependency array - only set up once

  // Handle view mission details
  const handleViewMission = (mission) => {
    console.log('View mission details:', mission);
    // TODO: Implement mission details view
    alert(`Mission Details\n\nMission: ${mission.missionName}\nRoute: ${mission.route?.start} → ${mission.route?.destination}\n\nFull mission viewer coming soon!`);
  };

  // Empty state
  if (!missions || missions.length === 0) {
    return (
      <div className={`${classes.card} border ${classes.border} rounded-xl p-8 mb-6`}>
        <div className="flex items-center gap-3 mb-4">
          <ClipboardList className="text-blue-400" size={24} />
          <h3 className={`text-xl font-bold ${classes.textPrimary}`}>Mission History</h3>
        </div>
        
        <div className="text-center py-12">
          <Package className={`mx-auto ${classes.textMuted} mb-4`} size={64} />
          <p className={`${classes.textPrimary} text-lg mb-2`}>
            No missions have been applied to this vehicle yet.
          </p>
          <p className={`${classes.textMuted}`}>
            Apply a mission from the Saved Missions page to start tracking vehicle usage and health changes.
          </p>
        </div>
      </div>
    );
  }

  // Determine how many missions to show
  const displayedMissions = showAll ? missions : missions.slice(0, 6);

  return (
    <div className={`${classes.card} border ${classes.border} rounded-xl p-6 mb-6`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <ClipboardList className="text-blue-400" size={24} />
        <h3 className={`text-xl font-bold ${classes.textPrimary}`}>Mission History</h3>
      </div>

      {/* Statistics Tiles */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Missions */}
          <div className={`${classes.secondary} rounded-lg p-4 border ${classes.border}`}>
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <ClipboardList className="text-white" size={20} />
              </div>
              <div>
                <p className={`${classes.textMuted} text-sm`}>Total Missions</p>
                <p className={`${classes.textPrimary} font-bold text-2xl`}>
                  {statistics.totalMissions}
                </p>
              </div>
            </div>
          </div>

          {/* Total Distance */}
          <div className={`${classes.secondary} rounded-lg p-4 border ${classes.border}`}>
            <div className="flex items-center gap-3">
              <div className="bg-green-600 p-2 rounded-lg">
                <TrendingUp className="text-white" size={20} />
              </div>
              <div>
                <p className={`${classes.textMuted} text-sm`}>Total Distance</p>
                <p className={`${classes.textPrimary} font-bold text-2xl`}>
                  {statistics.totalDistance} {statistics.distanceUnit}
                </p>
              </div>
            </div>
          </div>

          {/* Average Distance */}
          <div className={`${classes.secondary} rounded-lg p-4 border ${classes.border}`}>
            <div className="flex items-center gap-3">
              <div className="bg-purple-600 p-2 rounded-lg">
                <BarChart3 className="text-white" size={20} />
              </div>
              <div>
                <p className={`${classes.textMuted} text-sm`}>Average Distance</p>
                <p className={`${classes.textPrimary} font-bold text-2xl`}>
                  {statistics.averageDistance} {statistics.distanceUnit}
                </p>
              </div>
            </div>
          </div>

          {/* Last Mission */}
          <div className={`${classes.secondary} rounded-lg p-4 border ${classes.border}`}>
            <div className="flex items-center gap-3">
              <div className="bg-amber-600 p-2 rounded-lg">
                <Calendar className="text-white" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${classes.textMuted} text-sm`}>Last Mission</p>
                <p className={`${classes.textPrimary} font-semibold text-sm truncate`}>
                  {statistics.lastMission?.name || 'N/A'}
                </p>
                <p className={`${classes.textMuted} text-xs`}>
                  {statistics.lastMission?.date || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mission Cards Grid */}
      <div>
        <h4 className={`text-lg font-semibold ${classes.textPrimary} mb-4`}>
          Mission Details
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedMissions.map((mission, index) => (
            <MissionCard
              key={mission.missionId || mission.id || `mission-${index}`}
              mission={mission}
              onViewDetails={handleViewMission}
              isMetric={isMetric}
            />
          ))}
        </div>

        {/* Load More Button */}
        {missions.length > 6 && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowAll(!showAll)}
              className={`px-6 py-2 ${classes.buttonSecondary} rounded-lg transition-colors font-medium`}
            >
              {showAll ? 'Show Less' : `Show All ${missions.length} Missions`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleMissionHistory;
